/**
 * The one place the offline write queue is visible: the sentence at the top
 * of the app, and — when something needs a person rather than a clock — the
 * list underneath it.
 *
 * It draws nothing when there is nothing to say: an empty queue on a normal
 * connection is the ordinary state of this app, and a banner that is always
 * there stops meaning anything. `src/lib/queue.ts` is where every one of
 * these sentences is decided; this component only asks it and renders the
 * answer.
 */

import { useState } from 'react'
import { describeQueue, describeWrite, summarise } from '../lib/queue'
import { discardQueuedWrite, retryQueuedWrite, useOnline, useQueuedWrites } from '../queue-react'

export function QueueBanner() {
  const writes = useQueuedWrites()
  const online = useOnline()
  const [expanded, setExpanded] = useState(false)

  const summary = summarise(writes)
  const sentence = describeQueue(summary, online)
  if (!sentence) return null

  return (
    <div className={`queue-banner${summary.refused > 0 ? ' queue-banner--bad' : ''}`}>
      <p className="queue-banner__what">
        {sentence}
        {writes.length > 0 && (
          <button type="button" className="button--quiet" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Hide' : 'Show'}
          </button>
        )}
      </p>

      {expanded && (
        <ul className="queue-banner__list">
          {writes.map((write) => (
            <li key={write.id}>
              <span>{describeWrite(write)}</span>
              {write.refusal ? (
                <span className="queue-banner__row-actions">
                  <span className="bad note">{write.refusal.message}</span>
                  <button type="button" className="button--quiet" onClick={() => void retryQueuedWrite(write.id)}>
                    Try again
                  </button>
                  <button
                    type="button"
                    className="button--quiet"
                    onClick={() => void discardQueuedWrite(write.id)}
                  >
                    Discard
                  </button>
                </span>
              ) : (
                <span className="muted note">waiting</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
